import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, useTheme, Grid2 } from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import { useSearchParams } from "react-router-dom";

import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../models/SortType";
import {
  VendorsPOReportFilters,
  VendorsPOReportSelectedFilters,
  parseQueryParamsForVendorsPOReportFilters,
} from "../../../models/Vendors/VendorsPOReportFilters";
import CustomTextField from "../../components/common/textFields/TextField";
import VendorsPOReportTable from "../../components/common/table/VendorsPOReportTable";

const VENDORS_PO_REPORT_FILTERS_KEY = "VENDORS_PO_REPORT_FILTERS_KEY";

const VendorsPOReportView: React.FC = () => {
  const theme = useTheme();

  const { userStore, purchaseRequestStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string>("");
  const { showError } = useNotification();

  const initialFilters: VendorsPOReportFilters =
    parseQueryParamsForVendorsPOReportFilters(null, searchParams);

  const [filters, setFilters] =
    useState<VendorsPOReportFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<VendorsPOReportSelectedFilters>({
      ...initialFilters,
    });

  useEffect(() => {
    handleApply();
  }, []);

  useEffect(() => {
    updateSelectedFilters();
  }, [filters]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      VENDORS_PO_REPORT_FILTERS_KEY,
      JSON.stringify(filters),
    );

    setSelectedFilters((_) => ({
      ...filters,
    }));
  };

  const handleApply = async () => {
    if (!userStore.hasPermission(EmployeeRoles.PR_PO_VIEW)) {
      return;
    }

    if (filters.search?.trim().length === 0) {
      return;
    }

    const poIds = filters.search?.split(",").map((id) => id.trim()) || [];
    if (poIds.length === 0) {
      setSearchError("");
      return;
    }

    if (poIds?.length > 100) {
      setSearchError("You can only search up to 100 po ids at a time.");
      return;
    }

    setSearchError("");

    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        search: filters.search,
        sortBy: "created_date",
        sortType: SortType.DESC,
      }));

      await purchaseRequestStore.getVendorsPOReportData({
        sortBy: filters.sortBy,
        sortType: filters.sortType,
        search: poIds.join(",").trim(),
      });
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Failed to fetch the vendors po report details.",
      );
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleClearAllFilters = () => {
    setSearchError("");
    setFilters((_) => ({
      ...initialFilters,
    }));
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.PR_PO_VIEW) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box
            sx={{
              margin: "8px",
              gap: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
                gap: 2,
              }}
            >
              <Grid2
                container
                spacing={2}
                columns={12}
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  gap: 2,
                }}
              >
                <Grid2 size={{ lg: 9, md: 9, sm: 12, xs: 12 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <CustomTextField
                      width={"100%"}
                      placeholder="Enter comma separated po ids without spaces to search eg: 7463,2363,1924. Limit is 100 po ids."
                      value={filters.search}
                      onChange={(e) => {
                        setSearchError("");
                        setFilters((prev) => ({
                          ...prev,
                          search: e.target.value,
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleApply();
                        }
                      }}
                    />
                    {searchError && (
                      <CustomTypography
                        variant="body3"
                        style={{
                          color: theme.palette.customColors.errorRed,
                        }}
                      >
                        {searchError}
                      </CustomTypography>
                    )}
                  </Box>
                </Grid2>
                <Grid2
                  size={{ lg: 3, md: 3, sm: 12, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    gap: 2,
                  }}
                >
                  <AppButton
                    variant="primaryBlock"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ width: "100%" }}
                    disabled={loading}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear Filters"
                    customStyles={{ width: "100%" }}
                    onClick={() => handleClearAllFilters()}
                  />
                </Grid2>
              </Grid2>
            </Box>

            <Box sx={{ marginBottom: 2 }}>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>

            <VendorsPOReportTable
              data={purchaseRequestStore.vendorsPOReportData || []}
            />
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(VendorsPOReportView);
